import buildRequestFilter from "./buildRequestFilter";

function buildFrom(current, resultsPerPage) {
  if (!current || !resultsPerPage) return;
  return (current - 1) * resultsPerPage;
}

function buildSort(sortDirection, sortField, sortList) {
  if (sortList) {
    return sortList
  } else if (sortDirection && sortField) {
    return [{ [`${sortField}.keyword`]: sortDirection }];
  }
}

function buildMatch(searchTerm) {
  return searchTerm
    ? {
      multi_match: {
        query: searchTerm,
        fields: ["searchId^4.0", "description"],
      }
    }
    : { match_all: {} };
}

/*

  Converts current application state to an Elasticsearch request.

  When implementing an onSearch Handler in Search UI, the handler needs to take the
  current state of the application and convert it to an API request.

  For instance, there is a "current" property in the application state that you receive
  in this handler. The "current" property represents the current page in pagination. This
  method converts our "current" property to Elasticsearch's "from" parameter.

  This "current" property is a "page" offset, while Elasticsearch's "from" parameter
  is a "item" offset. In other words, for a set of 100 results and a page size
  of 10, if our "current" value is "4", then the equivalent Elasticsearch "from" value
  would be "40". This method does that conversion.

  We then do similar things for searchTerm, filters, sort, etc.
*/
export default function buildRequest(state) {
  const {
    current,
    filters,
    resultsPerPage,
    searchTerm,
    sortDirection,
    sortField,
    sortList
  } = state;

  const sort = buildSort(sortDirection, sortField, sortList);
  const match = buildMatch(searchTerm);
  const size = resultsPerPage > 0 ? resultsPerPage : 50;
  const from = buildFrom(current, resultsPerPage);
  const filter = buildRequestFilter(filters);

  const body = {
    aggs: {
      not_further_specified: { terms: { field: "not_further_specified"}},
      dataType: { terms: { field: "dataType.keyword", size: 5 } },
      foodClass: { terms: { field: "foodClass.keyword", size: 5 } },
      searchCategory: { terms: { field: "searchCategory.keyword", size: 10 } },
      searchBrand: {
        terms: {
          field: "searchBrand.keyword",
          size: 10
        }
      },
      searchPortion: {
        terms: {
          field: "searchPortion.keyword",
          size: 10
        }
      },
    },
    _source: true,
    fields: [
      "fdcId", "description", "foodPortions", "foodClass", "brandName", "brandOwner", "subBrandName", "not_further_specified"
    ],
    stored_fields: [
      "searchBrand", "searchCategory", "searchPortion"
    ],

    // Dynamic values based on current Search UI state
    // --------------------------
    // https://www.elastic.co/guide/en/elasticsearch/reference/7.x/full-text-queries.html
    query: {
      bool: {
        must: [match],
        ...(filter && { filter })
      }
    },
    // https://www.elastic.co/guide/en/elasticsearch/reference/7.x/search-request-sort.html
    ...(sort && { sort }),
    // https://www.elastic.co/guide/en/elasticsearch/reference/7.x/search-request-from-size.html
    ...(size && { size }),
    ...(from && { from })
  };

  return body;
}
